import {
  getReportById,
  getReportField,
  getReportNames,
  getReportRule,
  getRuleAssignment,
  searchRule,
  submitReportRequest,
} from "../../api/reportGeneratorApi";
import FormGenerator from "../../components/form-generator/FormGenerator";
import Button from "../../components/input/Button";
import React, { useEffect, useRef, useState } from "react";
import Dropdown from "../../components/input/Dropdown";
import {
  File,
  FileText,
  List,
  Package,
  Search,
  Clipboard,
} from "react-feather";
import { toast } from "react-toastify";
import ReportQueue from "./ReportQueue";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import RuleCreation from "./RuleCreation";
import Input from "../../components/input/Input";
import Tab from "../../components/Tab";
import RuleAssignment from "./RuleAssignment";
import SideDrawer from "../../components/input/SideDrawer";
import { getBulkDropdownData } from "../../api/keyvalue";
import LoadingSpinner from "../../components/LoadingSpinner";
import Grid from "../../components/Grid";
import { formatToDateOnlyFromJsonDate } from "../../dateConfig";

const ReportGenerator = () => {
  const reportGeneratorRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReport, setSelectedReport] = useState("");
  const [tab, setTab] = useState(1);
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [activeAccordion2, setActiveAccordion2] = useState(true);
  const [activeAccordion3, setActiveAccordion3] = useState(true); //** */
  const [formKey, setFormKey] = useState(0);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const isQueue = location.pathname.includes("/queue");
  const [queueKey, setQueueKey] = useState(0);
  const isRuleCreation = location.pathname.includes("/rule-creation"); //**
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [drawerTab, setDrawerTab] = useState(1);
  const [errors, setErrors] = useState({});

  const previousIsQueue = useRef(false);

  const [reportTypeId, setReportTypeId] = useState([]);
  const [report, setReport] = useState("");
  const [reportFields, setReportFields] = useState([]);
  const [reportRules, setReportRules] = useState([]);
  const [selectedField, setSelectedField] = useState("");
  const [selectedRule, setSelectedRule] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [reloadDates, setReloadDates] = useState(true);
  const [results, setResults] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);
  const [active, setActive] = useState(1);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [ruleAssignment, setRuleAssignment] = useState(null);

  const gridRef = useRef(null);

  useEffect(() => {
    if (location.pathname.includes("/mis/report-generator/queue")) {
      setTab(2);
      setQueueKey((prev) => prev + 1);

      if (!previousIsQueue.current) {
        setQueueKey((prev) => prev + 1);
      }
    } else {
      setTab(1);
    }
    previousIsQueue.current = location.pathname.includes(
      "/mis/report-generator/queue",
    );
  }, [location.pathname]);

  const submitHandler = async () => {
    try {
      setIsLoading(true);
      const reportSearchData = reportGeneratorRef.current?.getData();
      const errors = reportGeneratorRef.current?.validateSchema() ?? {};
      if (Object.keys(errors).length !== 0) {
        toast.error("Please fill all the required fields.", {
          autoClose: 5000,
          containerId: "Main",
        });
        return;
      }
      if (!reportSearchData?.REPORT?.REPORT_SEARCH) {
        toast.error("Invalid report data", {
          autoClose: 5000,
          containerId: "Main",
        });
        return;
      }

      const params = reportSearchData.REPORT.REPORT_SEARCH;
      const selectedReportObj = reportTypes.find(
        (r) => r.value === selectedReport,
      );

      if (!selectedReportObj) {
        toast.error("Invalid report type", {
          autoClose: 5000,
          containerId: "Main",
        });
        return;
      }
      const reportId = selectedReportObj.key;
      const response = await submitReportRequest(reportId, params, false);
      toast.success(response?.message, {
        autoClose: 5000,
        containerId: "Main",
      });
      reset();

      setIsLoading(false);
    } catch (error) {
        const serverMessage = error.response?.data?.message;
        const errorMessage = serverMessage || "Failed to submit report request";

        toast.error(errorMessage, {
            autoClose: 5000,
            containerId: "Main",
        });
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    (async () => {
      const response = await getReportNames();
      setReportTypes(response);
    })();
  }, []);

  const reset = () => {
    reportGeneratorRef.current?.resetData();
    setSelectedReport("");
  };

  useEffect(() => {
    (async () => {
      const response = await getReportById();
      setReportTypeId(response);
      const { active = [] } = await getBulkDropdownData([{ key: "active" }]);
      setActiveOptions(active);
    })();
  }, []);

  useEffect(() => {
    if (reloadDates) {
      const aWeekAgo = new Date();
      aWeekAgo.setDate(aWeekAgo.getDate() - 7);
      setFromDate(aWeekAgo.toISOString().split("T")[0]);
      setToDate(new Date().toISOString().split("T")[0]);
    }
    setReloadDates(false);
  }, [reloadDates]);

  const handleReportChange = async (value) => {
    setReport(value);
    setErrors({});
    setSelectedField("");
    setSelectedRule("");
    setReportFields([]);
    setReportRules([]);

    const selected = reportTypeId.find((r) => r.value === value);
    if (selected) {
      const fields = await getReportField(selected.value);
      setReportFields(fields);
      console.log("field details", fields);
    }
  };

  const handleFieldChange = async (value) => {
    setSelectedField(value);

    setSelectedRule("");
    setReportRules([]);

    const selected = reportFields.find((r) => r.value === value);
    if (report && selected) {
      const rules = await getReportRule(report, selected.field);
      setReportRules(rules);
    }
  };

  const handleSearch = async () => {
    const newErrors = {};
    if (!report) {
      newErrors.report = "Report Type is required";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setSearchSubmitLoading(true);
    setSearchClicked(true);
    try {
      const searchRequest = {
        reportId: report,
        fromDate: fromDate,
        toDate: toDate,
        status: active ? String(active) : "-2",
        ruleDef: selectedRule ? String(selectedRule) : "-2",
      };

      const response = await searchRule(searchRequest);

      if (response && response.reportData && response.reportData.length > 0) {
        const formattedData = response.reportData.map((report) => ({
          ...report,
          createdDateFormatted: report.createdDate
            ? formatToDateOnlyFromJsonDate(report.createdDate)
            : "",
        }));
        setResults(formattedData);
      }
    } catch (error) {
      console.error("Search failed", error);
    } finally {
      setSearchSubmitLoading(false);
    }
  };
  const handleRuleReset = () => {
    setReport("");
    setSelectedField("");
    setSelectedRule("");
    setReportFields([]);
    setReportRules([]);
    setActive(1);
    setReloadDates(true);
    setSearchClicked(false);
    setErrors({});
    setResults([]);
  };

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setDrawerTab(1);
  };
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleRuleSuccess = () => {
    handleDrawerClose();
    if (searchClicked) {
      handleSearch();
    }
  };

  const handleCellDoubleClick = async (props) => {
    try {
      const assignmentId = props.data.assignmentId;
      const response = await getRuleAssignment(assignmentId);

      console.log("get details", response?.reportRuleAssignment);
      setRuleAssignment(response);
      setEditMode(true);
      setDrawerTab(1);
      setIsDrawerOpen(true);
    } catch (error) {
      console.error("Failed to fetch rule assignment", error);
      toast.error("Failed to load rule details.", {
        autoClose: 5000,
        containerId: "Main",
      });
    }
  };

  const assignmentData = ruleAssignment?.reportRuleAssignment;

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div className="ui tabular menu attached">
          <div
            className={`item ${!isQueue && !isRuleCreation ? "active" : ""}`}
            onClick={() => navigate("/mis/report-generator")}
          >
            <span>
              <File size={15} style={{ marginRight: "5px" }} />
            </span>
            Report Generator
          </div>
          <div
            className={`item ${isQueue ? "active" : ""}`}
            onClick={() => navigate("/mis/report-generator/queue")}
          >
            <List size={15} style={{ marginRight: "5px" }} />
            Report Queue
          </div>

          <div
            className={`item ${isRuleCreation ? "active" : ""}`}
            onClick={() => navigate("/mis/report-generator/rule-creation")}
          >
            <Clipboard size={15} style={{ marginRight: "5px" }} />
            Rule Creation
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        ></div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: !isQueue && !isRuleCreation ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Report Configuration</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  reset();
                }}
              ></i>
            </div>
          </div>

          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            <form className="ui form">
              <h4 className="ui dividing header">Report Information</h4>
              <div className="three fields">
                <Dropdown
                  required={true}
                  label="Report Type"
                  placeholder="Select Report Type"
                  options={reportTypes}
                  value={selectedReport}
                  onChange={(e, { value }) => {
                    setSelectedReport(value);
                    setResetVisible(true);
                    setTimeout(() => {
                      reportGeneratorRef.current?.clearErrors?.();
                    }, 0);
                  }}
                />
              </div>

              {selectedReport && (
                <FormGenerator
                  key={selectedReport}
                  ref={reportGeneratorRef}
                  Actions={() => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        template="create"
                        isLoading={isLoading}
                        onClick={submitHandler}
                        create={true}
                        editMode={false}
                      />
                      <Button
                        template="reset"
                        isLoading={false}
                        onClick={reset}
                        editMode={false}
                      />
                    </div>
                  )}
                  templateName={selectedReport}
                />
              )}
            </form>
          </div>
        </div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: isQueue && !isRuleCreation ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion2 ? "active" : ""}`}
            onClick={() => setActiveAccordion2(!activeAccordion2)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Pending Reports</span>
                <FileText size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>
            </div>
          </div>
          <div className={`content ${activeAccordion2 ? "active" : ""}`}>
            <form className="ui form">
              <h4 className="ui dividing header">Report Approval Queue</h4>
              <ReportQueue key={queueKey} />
            </form>
          </div>
        </div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: isRuleCreation ? "block" : "none" }}
      >
        <div
          className="ui styled accordion"
          style={{ width: "100%", position: "relative" }}
        >
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              zIndex: 1,
            }}
          >
            <Button template="drawerOpen" onClick={handleDrawerOpen} />
          </div>
          <div
            className={`title ${activeAccordion3 ? "active" : ""}`}
            onClick={() => setActiveAccordion3(!activeAccordion3)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <span>Report Rules</span>
                <FileText size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>
            </div>
          </div>

          <div className={`content ${activeAccordion3 ? "active" : ""}`}>
            <form className="ui form">
              <h4 className="ui dividing header"> Report Rule Search</h4>
              <div className="three fields">
                <Dropdown
                  label="Report Type"
                  required={true}
                  placeholder="Select Report Type"
                  options={reportTypeId}
                  value={report}
                  onChange={(e, { value }) => handleReportChange(value)}
                  error={errors.report}
                />
                <Dropdown
                  label="Field"
                  placeholder="Select Report Field"
                  options={reportFields}
                  onChange={(e, { value }) => handleFieldChange(value)}
                  value={selectedField}
                  disabled={!report}
                />
                <Dropdown
                  label="Report Rule"
                  options={reportRules}
                  placeholder="Select Report Rule"
                  value={selectedRule}
                  disabled={!selectedField}
                  onChange={(e, { value }) => setSelectedRule(value)}
                />
              </div>
              <div className="two fields">
                <Dropdown
                  label="Active"
                  options={activeOptions}
                  value={active}
                  onChange={(e, { value }) => setActive(String(value))}
                />
                <Input
                  type="date"
                  label="From Date"
                  name="fromDate"
                  max={toDate ?? new Date().toISOString().split("T")[0]}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
                <Input
                  type="date"
                  label="To Date"
                  name="toDate"
                  min={fromDate}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div className="field" style={{ width: "100%" }}>
                  <Button template="search" onClick={handleSearch} />
                  <Button template="searchReset" onClick={handleRuleReset} />
                </div>
              </div>
              {searchClicked && (
                <>
                  {" "}
                  {searchSubmitLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <div>
                      <Grid
                        ref={gridRef}
                        heading="Report Rules"
                        content={results}
                        config={[
                          { headerName: "Rule Code", field: "assignmentCode" },
                          { headerName: "Report Name", field: "reportName" },
                          { headerName: "Rule Name", field: "ruleName" },
                          { headerName: "Field Name", field: "fieldName" },
                          { headerName: "Created By", field: "createdBy" },
                          {
                            headerName: "Created Date",
                            field: "createdDateFormatted",
                          },
                        ]}
                        onCellDoubleClicked={handleCellDoubleClick}
                      />
                    </div>
                  )}
                </>
              )}
            </form>
          </div>
        </div>
      </div>
      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <>
              <div className="employee-code-header"></div>
              <h3>
                Rule Assignment Code:{assignmentData?.reportAssignmentCode}
              </h3>
            </>
          ) : null
        }
        body={
          <form className="drawer-content">
            <div style={{ width: "60vw", boxShadow: "none" }}>
              <div className="ui top attached tabular menu">
                <Tab
                  Icon={Package}
                  onClick={() => {
                    setDrawerTab(1);
                  }}
                  active={drawerTab === 1}
                  label="Rule Assignment"
                  resizeForSmallerScreens={true}
                  showMandatory={true}
                  isMandatory={true}
                />
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: drawerTab === 1 ? "block" : "none" }}
              >
                <RuleAssignment
                  ruleAssignment={ruleAssignment}
                  editMode={editMode}
                  onSuccess={handleRuleSuccess}
                />
              </div>
            </div>
          </form>
        }
      />
    </>
  );
};

export default ReportGenerator;
